import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Wallet, Plus, Flame, Sparkles, RefreshCw, Crown, BookOpen, Video, Star } from 'lucide-react';

const META: Record<string, { label: string; icon: any; tone: string }> = {
  hot: { label: 'Hot', icon: Flame, tone: 'text-orange-500' },
  super_hot: { label: 'Super Hot', icon: Sparkles, tone: 'text-red-500' },
  signature: { label: 'Signature', icon: Crown, tone: 'text-amber-500' },
  refresh: { label: 'Refresh', icon: RefreshCw, tone: 'text-blue-500' },
  story: { label: 'Story', icon: BookOpen, tone: 'text-purple-500' },
  video_tour: { label: 'Video Tour', icon: Video, tone: 'text-pink-500' },
  featured_project: { label: 'Featured Project', icon: Star, tone: 'text-yellow-500' },
};

export function CreditWalletCard() {
  const { user } = useAuth();
  const { data } = useQuery({
    queryKey: ['credit-wallet', user?.id],
    enabled: !!user,
    queryFn: async () => {
      const { data } = await supabase
        .from('credit_wallet' as any)
        .select('credit_type, balance, expires_at')
        .eq('user_id', user!.id)
        .gt('balance', 0);
      const totals: Record<string, number> = {};
      ((data as any[]) || []).forEach((r) => {
        totals[r.credit_type] = (totals[r.credit_type] || 0) + r.balance;
      });
      return totals;
    },
  });

  const types = Object.keys(META);

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          <Wallet className="h-4 w-4 text-primary" /> Credit Wallet
        </CardTitle>
        <Button asChild size="sm" variant="default" className="gap-1">
          <Link to="/dashboard/credit-store"><Plus className="h-3.5 w-3.5" /> Top up</Link>
        </Button>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-7 gap-3">
          {types.map((t) => {
            const Icon = META[t].icon;
            const balance = data?.[t] || 0;
            return (
              <div key={t} className="rounded-lg border p-3 text-center">
                <Icon className={`h-5 w-5 mx-auto ${META[t].tone}`} />
                <p className="text-xs text-muted-foreground mt-1">{META[t].label}</p>
                <p className="text-lg font-bold tabular-nums">{balance}</p>
              </div>
            );
          })}
        </div>
        <p className="text-xs text-muted-foreground mt-3">
          À la carte credits never block your subscription quota — buy more anytime.
          {' '}<Link to="/dashboard/credit-store" className="text-primary font-medium hover:underline">Browse credit store →</Link>
        </p>
      </CardContent>
    </Card>
  );
}
